import { boolean, string, object, SchemaOf } from 'yup';

import { ResourceQuotaFormValues } from './types';

export function getResourceQuotaValidationSchema(
  memoryLimit: number,
  cpuLimit: number
): SchemaOf<ResourceQuotaFormValues> {
  return object({
    enabled: boolean().required('Resource quota enabled status is required.'),
    memoryRequest: string()
      .test(
        'non-negative-memory-request-validation',
        'Existing namespaces already have memory requests exceeding what is available in the cluster. Before you can set values here, you must reduce amounts in namespaces (and you may have to turn on over-commit temporarily to do so).',
        () => nonNegativeLimit(memoryLimit)
      )
      .test(
        'memory-request-validation',
        `Value must be between 0 and ${memoryLimit}.`,
        (value) => memoryValidation(value)
      ),
    memoryLimit: string()
      .test(
        'non-negative-memory-limit-validation',
        'Existing namespaces already have memory limits exceeding what is available in the cluster. Before you can set values here, you must reduce amounts in namespaces (and you may have to turn on over-commit temporarily to do so).',
        () => nonNegativeLimit(memoryLimit)
      )
      .test(
        'memory-limit-validation',
        `Value must be between 0 and ${memoryLimit}.`,
        (value) => memoryValidation(value)
      )
      .test(
        'memory-limit-not-less-than-request',
        'Memory limit cannot be less than memory request.',
        function memoryLimitValidation(value) {
          const memoryRequest = Number(this.parent.memoryRequest) || 0;
          const memoryLimitValue = Number(value) || 0;

          // If limit is 0, treat it as unlimited - skip validation
          if (memoryLimitValue === 0) {
            return true;
          }

          // Otherwise, limit must be >= request
          return memoryLimitValue >= memoryRequest;
        }
      ),
    cpuRequest: string()
      .test(
        'non-negative-cpu-request-validation',
        'Existing namespaces already have CPU requests exceeding what is available in the cluster. Before you can set values here, you must reduce amounts in namespaces (and you may have to turn on over-commit temporarily to do so).',
        () => nonNegativeLimit(cpuLimit)
      )
      .test(
        'cpu-request-validation',
        'CPU request value is required.',
        (value) => cpuValidation(value)
      ),
    cpuLimit: string()
      .test(
        'non-negative-cpu-limit-validation',
        'Existing namespaces already have CPU limits exceeding what is available in the cluster. Before you can set values here, you must reduce amounts in namespaces (and you may have to turn on over-commit temporarily to do so).',
        () => nonNegativeLimit(cpuLimit)
      )
      .test('cpu-limit-validation', 'CPU limit value is required.', (value) =>
        cpuValidation(value)
      )
      .test(
        'cpu-limit-not-less-than-request',
        'CPU limit cannot be less than CPU request.',
        function cpuLimitValidation(value) {
          const cpuRequest = Number(this.parent.cpuRequest) || 0;
          const cpuLimitValue = Number(value) || 0;

          // If limit is 0, treat it as unlimited - skip validation
          if (cpuLimitValue === 0) {
            return true;
          }

          // Otherwise, limit must be >= request
          return cpuLimitValue >= cpuRequest;
        }
      ),
  }).test(
    'resource-quota-validation',
    'At least a single limit must be set.',
    oneLimitSet
  );

  function oneLimitSet({
    enabled,
    memoryRequest,
    memoryLimit,
    cpuRequest,
    cpuLimit,
  }: Partial<ResourceQuotaFormValues>) {
    // If quota is disabled, this validation is not needed
    if (!enabled) {
      return true;
    }

    // Convert values to numbers, treating undefined/empty as 0
    const memReq = memoryRequest ? Number(memoryRequest) : 0;
    const memLim = memoryLimit ? Number(memoryLimit) : 0;
    const cpuReq = cpuRequest ? Number(cpuRequest) : 0;
    const cpuLim = cpuLimit ? Number(cpuLimit) : 0;

    // At least one value must be greater than 0
    return memReq > 0 || memLim > 0 || cpuReq > 0 || cpuLim > 0;
  }

  function nonNegativeLimit(limit: number) {
    return limit >= 0;
  }

  function memoryValidation(memoryValue?: string) {
    // If the value is undefined or empty, treat it as 0
    const memory = memoryValue ? Number(memoryValue) : 0;
    return memory >= 0 && memory <= memoryLimit;
  }

  function cpuValidation(cpuValue?: string) {
    // If the value is undefined or empty, treat it as 0
    const cpu = cpuValue ? Number(cpuValue) : 0;
    return cpu >= 0;
  }
}
